import { Add } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Checkbox, Grid2, Paper, Typography } from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMPopUp } from "@pimo/pimo-components";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import { Configuration, ExtendedFieldError, Project } from "product-test-types";
import { checkField, VIEW_NAMES } from "product-test-utils";
import { ChangeEvent, useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  getPluralOfProjectName,
  getProjectName,
} from "../../utils/get-plural-of-project-name";
import ProjectRow from "./project-row";

export type OEManagerProps = {
  language: SupportedLanguage;
};

export const ManageOEsSettingsPage: PimoReactComponent<OEManagerProps> = ({
  language,
}) => {
  const { setValue, formState } = usePimoForm<{
    projects?: Project[];
    configuration?: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const projectName = {
    single: getProjectName(formState?.configuration?.projectName, language),
    plural: getPluralOfProjectName(
      formState?.configuration?.projectName,
      language
    ),
  };

  const [openDelete, setOpenDelete] = useState(false);

  const [currentOeOnCreateEditDelete, setCurrentOeOnCreateEditDelete] =
    useState<string[] | undefined>(undefined);

  const [selectedProjectIndices, setSelectedProjectIndices] = useState<
    string[]
  >([]);

  const handleAppend = () => {
    const newProject: Project = {
      id: uuidv4(),
      name: "",
      customId: "",
      description: "",
      owner: "",
      spentBudget: 0,
      flag: "create",
    };

    setValue("projects", [...(formState.projects ?? []), newProject]);
    setValue("enableSaveButton", true);
    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (projects: string[]) => {
    setCurrentOeOnCreateEditDelete(projects);
    setOpenDelete(true);
  };

  const handleConfirmDelete = () => {
    if (currentOeOnCreateEditDelete === undefined) {
      return;
    }

    setValue(
      "projects",
      formState.projects?.map((item) =>
        item.id && currentOeOnCreateEditDelete?.includes(item.id)
          ? { ...item, flag: "delete" }
          : item
      )
    );
    setValue("enableSaveButton", true);
    setOpenDelete(false);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setCurrentOeOnCreateEditDelete(undefined);
  };

  const handleProjectSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectedProjectIndices(
      isChecked ? (formState.projects?.map((p) => p.id) ?? []) : []
    );
  };

  const handleProjectFieldChange = useCallback(
    (id: string, fieldKey: string, value: unknown) => {
      const getFieldDefinition = (fieldName: string) => {
        return formState.configuration?.fieldsDefinition
          ?.filter((field) => field.purpose === "projectBasic")
          ?.find((f) => f.fieldName === fieldName && f.enabled === true);
      };
      const getFieldIndexUsingId = (fieldId: string) => {
        return formState.projects?.findIndex((p) => p.id === fieldId);
      };

      const index = getFieldIndexUsingId(id);
      if (index === -1 || index === undefined) return;

      const newErrors = { ...(formState.validationErrors ?? {}) } as Record<
        string,
        ExtendedFieldError
      >;

      const setFlagIfNeeded = () => {
        const p = formState.projects?.[index];
        if (p && p.flag !== "create" && p.flag !== "delete") {
          setValue(`projects.${index}.flag`, "update");
        }
      };

      const def = getFieldDefinition(fieldKey);

      switch (fieldKey) {
        case "name":
        case "customId":
        case "description":
        case "owner":
        case "freeTextFieldOne": {
          setValue(`projects.${index}.${fieldKey}`, value);

          checkField({
            iterableFieldName: `projects.{idx}.${fieldKey}`,
            idx: index,
            setErrors: (errors) => setValue("validationErrors", errors),
            errors: newErrors,
            value,
            tabContainingErrors: VIEW_NAMES[5],
            language,
          });

          setFlagIfNeeded();
          break;
        }
        case "approvedBudget": {
          const raw = value as string;
          setValue(`projects.${index}.approvedBudget`, raw);

          checkField({
            iterableFieldName: `projects.{idx}.approvedBudget`,
            idx: index,
            setErrors: (errors) => setValue("validationErrors", errors),
            errors: newErrors,
            value,
            tabContainingErrors: VIEW_NAMES[5],
            language,
          });

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "startDate": {
          const dateVal = value as
            | dayjs.Dayjs
            | string
            | number
            | Date
            | null
            | undefined;
          setValue(`projects.${index}.startDate`, dateVal);

          let entry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            entry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            };
          } else if (dateVal && dayjs(dateVal).isValid() === false) {
            entry = {
              type: "invalid",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            };
          }

          if (entry) {
            newErrors[`projects.${index}.startDate`] = entry;
          } else {
            delete newErrors[`projects.${index}.startDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "endDate": {
          const dateVal = value as
            | dayjs.Dayjs
            | string
            | number
            | Date
            | null
            | undefined;
          setValue(`projects.${index}.endDate`, dateVal);

          let newEntry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            newEntry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            };
          } else if (dateVal && !dayjs(dateVal).isValid()) {
            newEntry = {
              type: "invalid",
              message: "This is not a valid date",
              tabContainingErrors: VIEW_NAMES[5],
            };
          } else if (
            dateVal &&
            formState.projects?.[index]?.startDate &&
            dayjs(dateVal).isBefore(
              dayjs(formState.projects?.[index]?.startDate)
            )
          ) {
            newEntry = {
              type: "invalid",
              message: "End date should be after start date",
              tabContainingErrors: VIEW_NAMES[5],
            };
          }

          if (newEntry) {
            newErrors[`projects.${index}.endDate`] = newEntry;
          } else {
            delete newErrors[`projects.${index}.endDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "category":
        case "secondCategory": {
          const val = value as string;
          setValue(`projects.${index}.${fieldKey}`, val);

          if (!val && def?.isMandatory) {
            newErrors[`projects.${index}.${fieldKey}`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`projects.${index}.${fieldKey}`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        default: {
          setValue(`projects.${index}.${fieldKey}`, value);
          setFlagIfNeeded();
          break;
        }
      }
      if (formState.enableSaveButton !== true) {
        setValue(`enableSaveButton`, true);
      }
    },
    [
      formState.validationErrors,
      formState.enableSaveButton,
      formState.configuration?.fieldsDefinition,
      formState.projects,
      language,
      setValue,
    ]
  );

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "scroll",
      }}
    >
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language, "manageProjects.title", {
          projects: projectName.plural,
        })}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          {getLocalizedMessage(language, "manageProjects.listTitle", {
            projects: projectName.plural,
          })}
        </Typography>
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
          }}
        >
          <Grid2
            container
            alignItems="center"
            sx={{
              eight: 65,
              p: 2,
              borderBottom: "1px solid",
              borderColor: grey[300],
            }}
          >
            <Grid2 size={0.5}>
              <Checkbox
                onChange={handleProjectSelectionChange}
                icon={
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      border: "2px solid",
                      borderColor: grey[700],

                      display: "inline-block",
                      backgroundColor: "#fff",
                    }}
                  />
                }
                checkedIcon={
                  <span
                    style={{
                      width: 20,
                      height: 20,

                      border: "2px solid",
                      borderColor: grey[700],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff",
                    }}
                  >
                    <CheckIcon
                      style={{
                        fontSize: 18,
                        color: grey[700],
                      }}
                    />
                  </span>
                }
                checked={
                  selectedProjectIndices?.length === formState.projects?.length
                }
              />
            </Grid2>
            <Grid2 size={8.5} sx={{ fontSize: "14px", fontWeight: 500 }}>
              {getLocalizedMessage(language, "manageProjects.projectName", {
                project: projectName.single,
              })}
            </Grid2>
            <Grid2
              size={3}
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
            >
              {selectedProjectIndices?.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => handleDelete(selectedProjectIndices)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  }}
                >
                  {getLocalizedMessage(language, "deleteSelected")}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleAppend}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "7px 16px",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
                startIcon={<Add />}
              >
                {getLocalizedMessage(language, "manageProjects.addProjects", {
                  projectNamePlural: projectName.plural,
                })}
              </Button>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              width: "100%",
            }}
          >
            {formState.projects
              ?.filter((project) => project.flag !== "delete")
              .map((field) => (
                <ProjectRow
                  key={field.id}
                  field={field}
                  formState={formState}
                  setValue={setValue}
                  handleDelete={handleDelete}
                  selectedProjectIndices={selectedProjectIndices}
                  setSelectedProjectIndices={setSelectedProjectIndices}
                  projectName={projectName}
                  onFieldChange={handleProjectFieldChange}
                  language={language}
                />
              ))}
            {(formState.projects?.length ?? 0) > 6 && (
              <Box sx={{ height: 40 }} id={"dummy-to-scroll-into"}></Box>
            )}
          </Box>
        </Paper>
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
          <Button
            variant="outlined"
            onClick={handleAppend}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              padding: "7px 16px",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
            startIcon={<Add />}
          >
            {getLocalizedMessage(language, "manageProjects.addProjects", {
              projectNamePlural: projectName.plural,
            })}
          </Button>
        </Box>

        <PMPopUp
          open={openDelete}
          title={
            getLocalizedMessage(language, "dialog.title.confirmDelete") ||
            "Confirm Delete"
          }
          description={
            currentOeOnCreateEditDelete?.length === 1
              ? getLocalizedMessage(language, "dialog.message.genericConfirm", {
                  itemName: projectName.single,
                })
              : getLocalizedMessage(
                  language,
                  "dialog.message.genericConfirmMulti",
                  {
                    itemCount: currentOeOnCreateEditDelete?.length ?? 0,
                    itemName: projectName.plural,
                  }
                )
          }
          onClose={() => handleCloseDelete()}
          maxWidth="sm"
          cancelButtonText={
            getLocalizedMessage(language, "dialog.button.cancel") || "Cancel"
          }
          cancelButtonFireEvent={() => handleCloseDelete()}
          cancelButtonColor="white"
          confirmationButtonColor="error"
          confirmationButtonText={
            getLocalizedMessage(language, "dialog.button.delete") || "Delete"
          }
          confirmationButtonFireEvent={() => {
            handleConfirmDelete();
          }}
        />
      </Box>
    </Box>
  );
};
